import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { getFixedSizeImage } from '@gem/modules/image-resizer';

import { ChatBackgroundImageSize, ChatBackgroundType } from 'APP/constants/appearance';
import { TChatBackground } from 'APP/store/Appearance/Appearance.types';
import Entities from 'STORE';

interface IChatBackgroundImagePresenterParams {
  background: TChatBackground;
  size: ChatBackgroundImageSize;
}

interface IChatBackgroundImagePresenter {
  background: TChatBackground;
  url: string | null;
  imageUrl: string | null;
  setBackground(value: TChatBackground): void;
}

const IMAGE_MAX_SIZE_MAP: Record<ChatBackgroundImageSize, number> = {
  [ChatBackgroundImageSize.Original]: 2960,
  [ChatBackgroundImageSize.Preview]: 1280,
  [ChatBackgroundImageSize.Miniature]: 300,
};

export const useChatBackgroundImagePresenter = ({
  background,
  size,
}: IChatBackgroundImagePresenterParams): IChatBackgroundImagePresenter => {
  const presenter = useLocalObservable<IChatBackgroundImagePresenter>(() => ({
    background: background,

    get url(): string | null {
      if (presenter.background.type === ChatBackgroundType.Custom) {
        return (
          Entities.appearance.getCustomChatBackground(presenter.background.background)?.url || null
        );
      } else if (presenter.background.type === ChatBackgroundType.Wallpaper) {
        return Entities.clientConfig.backgroundsMap[presenter.background.background]?.url || null;
      }
      return null;
    },

    get imageUrl(): string | null {
      if (presenter.url) {
        return getFixedSizeImage(presenter.url, {
          width: IMAGE_MAX_SIZE_MAP[size],
          height: IMAGE_MAX_SIZE_MAP[size],
        });
      }
      return null;
    },

    setBackground(value: TChatBackground): void {
      presenter.background = value;
    },
  }));

  useEffect(() => {
    presenter.setBackground(background);
  }, [background]);

  return presenter;
};
